type QueuedRequest = {
  resolve: (release: () => void) => void;
  reject: (error: DOMException) => void;
  signal: AbortSignal | null | undefined;
};

/**
 * Small FIFO concurrency gate for browser requests.
 * Permits are always represented by an idempotent release callback so callers
 * can safely release them from `finally` blocks.
 */
export class RequestAdmissionGate {
  private active = 0;
  private readonly queue: QueuedRequest[] = [];

  constructor(private readonly limit: number) {
    if (!Number.isInteger(limit) || limit < 1) {
      throw new Error('Request admission limit must be a positive integer');
    }
  }

  async acquire(signal?: AbortSignal | null): Promise<() => void> {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    if (this.active < this.limit) {
      this.active += 1;
      return this.createRelease();
    }

    return new Promise<() => void>((resolve, reject) => {
      const queued: QueuedRequest = { resolve, reject, signal };
      const handleAbort = () => {
        const index = this.queue.indexOf(queued);
        if (index >= 0) {
          this.queue.splice(index, 1);
          reject(new DOMException('Aborted', 'AbortError'));
        }
      };
      signal?.addEventListener('abort', handleAbort, { once: true });
      queued.resolve = (release) => {
        signal?.removeEventListener('abort', handleAbort);
        resolve(release);
      };
      this.queue.push(queued);
    });
  }

  private createRelease(): () => void {
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.active -= 1;
      this.admitNext();
    };
  }

  private admitNext(): void {
    while (this.active < this.limit) {
      const queued = this.queue.shift();
      if (!queued) return;
      if (queued.signal?.aborted) {
        queued.reject(new DOMException('Aborted', 'AbortError'));
        continue;
      }
      this.active += 1;
      queued.resolve(this.createRelease());
    }
  }
}
